import React, { useState, useEffect } from 'react';
import { Phone, ChevronDown, Search, Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { services } from '../mock';

const aboutLinks = [
  { label: 'Home', description: 'Clinic overview and patient-first care.', to: '/' },
  { label: 'About', description: 'Our story, values, and approach.', to: '/about' },
  { label: 'Our Team', description: 'Meet the physiotherapists and care team.', to: '/team' },
  { label: 'Location', description: 'Find us on Tecumseh Road East.', to: '/locations' },
  { label: 'Contact', description: 'Phone, email, map, and clinic details.', to: '/contact' },
];

const BrandLogo = () => (
  <Link to="/" className="flex items-center shrink-0" aria-label="Uptown Physiotherapy Clinic home">
    <img
      src="/uptown-logo.svg"
      alt="Uptown Physiotherapy Clinic"
      className="h-14 w-14 object-contain"
    />
  </Link>
);

const MegaLink = ({ to, label, description }) => (
  <Link to={to} className="block rounded-lg px-3 py-2.5 transition-colors hover:bg-neutral-50">
    <span className="block text-[14px] font-semibold text-neutral-950">{label}</span>
    <span className="mt-0.5 block text-[12px] leading-snug text-neutral-500">{description}</span>
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [pagesOpen, setPagesOpen] = useState(false);
  const routerLoc = useLocation();

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setPagesOpen(false);
    window.scrollTo(0, 0);
  }, [routerLoc.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md">
      <div className="max-w-[1240px] mx-auto px-6 py-4">
        <div className="relative flex items-center justify-between gap-5 rounded-full border border-neutral-200 bg-white px-5 py-3 shadow-sm">
          <BrandLogo />
          <nav className="hidden lg:flex items-center gap-7 text-[14px] font-medium text-neutral-900">
          <div className="relative py-3 -my-3" onMouseEnter={() => setPagesOpen(true)} onMouseLeave={() => setPagesOpen(false)}>
            <button className="flex items-center gap-1 hover:text-neutral-500 transition-colors">
              About Us <ChevronDown size={14} />
            </button>
            {pagesOpen && (
              <div className="absolute top-full left-0 w-[720px] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl">
                <div className="grid grid-cols-[1.2fr_1fr]">
                  <div className="p-6">
                    <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#CA0028]">Clinic</p>
                    <div className="grid grid-cols-2 gap-1">
                      {aboutLinks.map((item) => (
                        <MegaLink key={item.label} {...item} />
                      ))}
                    </div>
                  </div>
                  <div className="border-l border-neutral-100 bg-neutral-50 p-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">Book by phone</p>
                    <h3 className="mt-4 text-[22px] font-bold leading-tight tracking-tight text-neutral-950">
                      Call our Windsor clinic to book.
                    </h3>
                    <p className="mt-3 text-[13px] leading-relaxed text-neutral-600">
                      Our reception team can help choose the right service, therapist, and appointment time.
                    </p>
                    <a href="tel:+15192522752" className="mt-5 inline-flex rounded-full bg-[#CA0028] px-5 py-2.5 text-[13px] font-medium text-white transition-colors hover:bg-[#A80021]">
                      +1-519-252-2752
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="relative py-3 -my-3" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
              <Link to="/services" className="flex items-center gap-1 hover:text-neutral-500 transition-colors">
                Services <ChevronDown size={14} />
              </Link>
              {servicesOpen && (
                <div className="absolute top-full left-0 w-[920px] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl">
                  <div className="grid grid-cols-[1fr_1fr_1fr_260px]">
                    <div className="p-6">
                      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#CA0028]">Core Care</p>
                      {services.slice(0, 5).map((service) => (
                        <MegaLink key={service.slug} to={`/services/${service.slug}`} label={service.name} description={service.short} />
                      ))}
                    </div>
                    <div className="border-l border-neutral-100 p-6">
                      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#CA0028]">Rehab</p>
                      {services.slice(5, 10).map((service) => (
                        <MegaLink key={service.slug} to={`/services/${service.slug}`} label={service.name} description={service.short} />
                      ))}
                    </div>
                    <div className="border-l border-neutral-100 p-6">
                      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#CA0028]">Specialized</p>
                      {services.slice(10).map((service) => (
                        <MegaLink key={service.slug} to={`/services/${service.slug}`} label={service.name} description={service.short} />
                      ))}
                    </div>
                    <div className="border-l border-neutral-100 bg-neutral-50 p-6">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">Need help choosing?</p>
                      <h3 className="mt-4 text-[20px] font-bold leading-tight tracking-tight text-neutral-950">
                        Speak with reception first.
                      </h3>
                      <p className="mt-3 text-[13px] leading-relaxed text-neutral-600">
                        Tell us what is bothering you and we will point you toward the right appointment.
                      </p>
                      <a href="tel:+15192522752" className="mt-5 inline-flex rounded-full bg-[#CA0028] px-5 py-2.5 text-[13px] font-medium text-white transition-colors hover:bg-[#A80021]">
                        Call to Book
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Link to="/team" className="link-underline">Our Team</Link>
            <Link to="/contact" className="link-underline">Free Injury Screen</Link>
            <Link to="/locations" className="link-underline hover:text-neutral-500">Find A Clinic</Link>
            <Link to="/news" className="link-underline">News</Link>
            <Link to="/contact" className="link-underline hover:text-neutral-500">Contact</Link>
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:+15192522752" className="flex items-center gap-2 text-sm font-medium">
              <Phone size={16} className="text-[#CA0028]" />
              <span>+1-519-252-2752</span>
            </a>
            <a href="tel:+15192522752" className="bg-[#CA0028] text-white text-[13px] font-medium px-5 py-2.5 rounded-full hover:bg-[#A80021] transition-colors">
              Call to Book
            </a>
            <Link to="/contact" className="w-10 h-10 rounded-full border border-neutral-200 grid place-items-center hover:bg-neutral-50 transition-colors">
              <Search size={16} />
            </Link>
          </div>
          <button onClick={() => setOpen(!open)} className="lg:hidden p-2">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-neutral-100 bg-white">
          <div className="px-6 py-4 flex flex-col gap-1 text-[15px]">
            {[
              { l: 'Home', p: '/' },
              { l: 'About', p: '/about' },
              { l: 'Services', p: '/services' },
              { l: 'Our Team', p: '/team' },
              { l: 'Locations', p: '/locations' },
              { l: 'News', p: '/news' },
              { l: 'Contact', p: '/contact' },
              { l: 'Call to Book', p: 'tel:+15192522752' },
            ].map(({ l, p }) => (
              p.startsWith('tel:') ? (
                <a key={l} href={p} className="py-3 border-b border-neutral-100">{l}</a>
              ) : (
                <Link key={l} to={p} className="py-3 border-b border-neutral-100">{l}</Link>
              )
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
